/**
 * karmabooking-api
 *
 * @link      https://karmabunny.com.au
 * @copyright Copyright (c) 2020 Karmabunny
 */

import { newApiError, newTimeoutError } from "./errors";

/** Default 45 seconds. */
const TIMEOUT = 45000;

export type Method = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

export type Query = Record<string, any>;

export interface ApiRequest {
    method: string;
    endpoint: string;
    query?: Query;
    headers?: Record<string, string>,
    body?: any;
}

/**
 *
 * @param params
 */
export function stringify(params?: Record<string, any>): string {
    if (!params) return '';

    let query = [] as string[];

    for (let key in params) {
        if (!params.hasOwnProperty(key)) continue;
        if (typeof params[key] === 'undefined') continue;

        key = encodeURIComponent(key);
        let value = encodeURIComponent(params[key]);

        query.push(`${key}=${value}`);
    }

    return query.length ? '?' + query.join('&') : '';
}

async function sleep(timeout: number) {
    return new Promise<void>(resolve => {
        window.setTimeout(resolve, timeout);
    })
}

/**
 *
 *
 * Errors galore!:
 * - ApiError on 400+ status codes
 * - TimeoutError if we're too slow (looking at you RMS)
 * - SyntaxError on malformed JSON
 * - TypeError if the network is dead
 *
 * @param req
 * @throws {ApiError|TimeoutError|SyntaxError|TypeError}
 */
export async function api(req: ApiRequest) {

    const abort = new AbortController();

    const { url, key } = window.__config;
    const { endpoint, method, body } = req;

    const params = stringify(req.query);
    const headers = {
        'Authorization': `Bearer ${key}`,
        'Content-Type': 'application/json; charset=UTF-8',
        'Accept': 'application/json',
        'Accept-Language': window.__translations.lang,
        ...req.headers,
    }

    return await Promise.race([
        // Do a request, although catch status code errors and throw them.
        // Always convert to JSON.
        (async () => {
            const res = await fetch(url + endpoint + params, {
                method, headers, body,
                signal: abort.signal,
            });

            if (!res.ok) throw await newApiError(req, res);

            // TODO we could conditionally convert to JSON based on the
            // 'Accept' header.
            return await res.json();
        })(),

        // Race it. We're on the clock.
        (async () => {
            await sleep(TIMEOUT);
            abort.abort();
            throw newTimeoutError(url, req, TIMEOUT);
        })(),
    ]);
}
